/**
 * THE SAVE ORDER for every payment-types screen:
 *   1. the location's grants (gateway PUT) — changed fields only
 *   2. each register (payment-types PUT) — changed fields only
 *   3. the broadcast to other tills, if one was chosen and its source saved
 *
 * Each step that fails becomes its own error line; nothing is swallowed and a
 * later step still runs, so one refusal never hides whether the rest landed.
 * No browser-side push: the server pushes every till it changed.
 */
import { api } from '../../../services/apiService';
import type { BulkResult } from './SaveBar';
import { explainPaymentError } from './errors';

/** A refusal in words — known codes become sentences, never a bare code. */
const why = (e: any, fallback: string) => explainPaymentError(e, fallback);

export interface SaveRequest {
  tenantId: string;
  locationId?: string;
  locationName?: string;
  grantPatch: Record<string, boolean> | null;
  registers: Array<{ terminalId: string; name: string; patch: Record<string, unknown> | null }>;
  /** Runs after the register it copies from has saved. */
  broadcast?: { sourceTerminalId: string; run: () => Promise<BulkResult[]> } | null;
}

export interface SaveOutcome {
  errors: string[];
  grantsSaved: boolean;
  savedTerminals: string[];
  bulkResults: BulkResult[] | null;
}

export async function saveChanges(req: SaveRequest): Promise<SaveOutcome> {
  const out: SaveOutcome = { errors: [], grantsSaved: false, savedTerminals: [], bulkResults: null };

  if (req.grantPatch && req.locationId) {
    try {
      await api.posNmi.putPaymentGateway(req.tenantId, req.locationId, req.grantPatch);
      out.grantsSaved = true;
    } catch (e) {
      out.errors.push(`${req.locationName || 'Location'} grants were not saved: ${why(e, 'the service refused the change.')}`);
    }
  }

  for (const r of req.registers) {
    if (!r.patch) continue;
    try {
      await api.posNmi.putPaymentTypes(req.tenantId, r.terminalId, r.patch as any);
      out.savedTerminals.push(r.terminalId);
    } catch (e) {
      out.errors.push(`${r.name} was not saved: ${why(e, 'the service refused the change.')}`);
    }
  }

  if (req.broadcast) {
    if (!out.savedTerminals.includes(req.broadcast.sourceTerminalId)) {
      out.errors.push('Nothing was sent to the other tills, because this till did not save.');
    } else {
      try {
        out.bulkResults = await req.broadcast.run();
      } catch (e) {
        out.errors.push(`This till saved, but the other tills were not changed: ${why(e, 'the bulk update was refused.')}`);
      }
    }
  }

  return out;
}
